import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, WRITE_FLAGS } from "./_shared.js";

/**
 * `EXP-APPROVE` (@UC0217) — trang chat **T2**: duyệt hoặc từ chối một khoản chi đang chờ.
 * QĐ PO 17/09 #1 (`US-AIV2-0055`). Trước task này trang là **redirect** sang màn nhóm.
 *
 * 🔴 CÙNG HÌNH `PAY-CONFIRM-DECIDE`: **một việc, một ô `quyetDinh`** — không phải hai trang
 * "duyệt" và "từ chối". Người dùng nói *"duyệt khoản chi của Bình"* hay *"từ chối khoản đó"* đều vào
 * đây; ô enum quyết đi nhánh nào. `choices` viết bằng **chữ người đọc** (không `approve`/`reject`):
 * phiếu nội suy `{quyetDinh}` nên người dùng phải đọc được nó (tiền lệ #811 M1).
 *
 * 🔴 VAI = O/A/T (`MANAGER_ROLES`), **và người ứng KHÔNG tự duyệt khoản của mình** — vế thứ hai KHÔNG
 * phải việc của trang: `ExpenseDecisionService._approveInTx` gác, đọc **`payerMemberId`** (KHÔNG phải
 * `createdBy` — hai cột khác nhau, §3.6 sinh ra để chặn đúng nhầm lẫn đó) và ném
 * `EXPENSE_SELF_APPROVE_FORBIDDEN`. Chốt nằm ở LÕI nên cửa GUI đi qua cùng một phép kiểm; hai phép
 * kiểm cho một luật sẽ trôi khỏi nhau. Trang chỉ mời đúng vai; luật "tự duyệt" ở cửa.
 *
 * 🔴 KHOẢN CHI CÓ **BA** KẾT CỤC, không phải hai: `posted` (tiền rời quỹ ngay) · `cooling` (đã duyệt
 * nhưng còn chờ hết thời gian cân nhắc — **0 bút toán lúc duyệt**) · `rejected`. Câu `done` PHẢI nói
 * đúng kết cục qua `{ketQua}` — khai "đã chi" cho một khoản đang `cooling` là nói sai về tiền
 * (cùng lớp lỗi đã vá ở `EXP-F-NOALLOC` #871 và `EXP-F-EQUAL` #905).
 */
export const EXP_APPROVE = {
  id: "EXP-APPROVE",
  uc: "@UC0217",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    { id: "khoanChi", type: "text", required: true, ask: "Khoản chi nào? (nội dung hoặc người ứng)" },
    // review #1036 HIGH-2: "không/chưa duyệt" từng khớp NHÃN "duyệt" ⇒ duyệt. `guardedChoices`: câu phủ định/nghi vấn không bao giờ ra "duyệt".
    { id: "quyetDinh", type: "enum", required: true, ask: "Duyệt hay từ chối?", choices: ["duyệt", "từ chối"], guardedChoices: ["duyệt"] },
    { id: "lyDo", type: "text", required: false, ask: "Lý do từ chối (người tạo khoản chi sẽ đọc được)?", textForm: "note" },
  ],
  llm: {
    description:
      "Duyệt hoặc từ chối một KHOẢN CHI (khoản tiền đã tiêu) đang chờ quyết định của nhóm. Đối tượng được duyệt/từ chối ở đây luôn là MỘT KHOẢN TIỀN; nếu câu nói về việc duyệt/từ chối MỘT NGƯỜI xin vào nhóm thì đó là GRP-JOIN-REQUEST.",
    examples: [
      "duyệt khoản chi tiền nước",
      "duyet khoan chi tien nuoc",
      "đồng ý khoản chi của Bình",
      "dong y khoan chi cua Binh",
      "từ chối khoản chi liên hoan",
      "tu choi khoan chi lien hoan",
      "duyệt chi 500k mua bóng",
      "không duyệt khoản này",
      "khoản chi tiền nước thì duyệt",
      "từ chối vì chưa có hoá đơn",
      "ok khoản chi đó",
      // 🔴 0102 — hai câu TẮT bù cho `"duyệt đi"` đã chuyển sang `clarifyExamples`. Khác nhau ở chỗ
      // hai câu này NÓI RÕ duyệt cái gì ("khoản chi"), nên chúng vẫn dạy model chọn đúng trang;
      // bất biến ≥2 câu nói tắt là để model quen với cách người Việt gõ, không phải để đủ số.
      "duyệt chi đi",
      "chi này ok",
      // ——— 0105: thêm cho đủ 40 ———
      "duyet chi 500k mua bong",
      "khong duyet khoan nay",
      "khoan chi tien nuoc thi duyet",
      "tu choi vi chua co hoa don",
      "ok khoan chi do",
      "duyet chi di",
      "chi nay ok",
      "duyệt khoản chi mua quà",
      "duyet khoan chi mua qua",
      "từ chối khoản chi đang chờ",
      "tu choi khoan chi dang cho",
      "đồng ý cho chi khoản này",
      "dong y cho chi khoan nay",
      "khoản chi đó không hợp lý, từ chối giúp",
      "khoan chi do khong hop ly tu choi giup",
      "duyệt khoản chi tiền điện tháng này",
      "duyet khoan chi tien dien thang nay",
      "không đồng ý khoản chi liên hoan",
      "khong dong y khoan chi lien hoan",
      "duyệt cho khoản chi mua bàn ghế",
      "duyet cho khoan chi mua ban ghe",
      "từ chối khoản chi vì quá tay",
      "tu choi khoan chi vi qua tay",
      "khoản chi của An mình duyệt nhé",
      "khoan chi cua an minh duyet nhe",
      "bác khoản chi đó đi",
      "bac khoan chi do di",
      "duyệt khoản chi chờ lâu rồi",
      "duyet khoan chi cho lau roi",
      "khoản chi photo tài liệu, duyệt",
    ],
    /*
     * 🔴 US-AIV2-0102 (@UC0176 · QĐ PO 23/09) — câu mà HỎI LẠI mới đúng.
     * "duyệt đi" không nói duyệt CÁI GÌ: khoản chi (trang này) · người xin vào nhóm
     * (`GRP-JOIN-REQUEST`) · lời báo chuyển khoản (`PAY-CONFIRM-DECIDE`). Một trong ba nhánh ấy
     * dẫn tới bút toán tiền ⇒ đoán bừa là đúng thứ `@UC0171` cấm.
     * Trước 0102, câu này nằm trong `examples` nên cổng PHẠT model vì đã hỏi lại.
     */
    clarifyExamples: ["duyệt đi"],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["có việc gì đang chờ tôi không?"],
  },
  executor: "expenseDecision.decide",
  /**
   * `@UC0216` — ba cửa trang này chịu trách nhiệm.
   *
   * 🔴 `GET /expenses/{expenseId}` phải GIỮ ở đây: nó là cửa ĐỌC mà phiếu dùng để dựng
   * `{tenKhoanChi}`/`{soTien}`/`{tenNguoiUng}`. Gỡ nó ⇒ bản đồ cửa↔trang thủng (gate `assert-ai-coverage`
   * bắt: *"cửa không có trang"*), và người sau sẽ tưởng màn hình nào đó đang gọi nó.
   *
   * 🔴 `POST /expenses/{expenseId}/reject` **KHÔNG có trong danh sách này**, dù binding của trang có gọi
   * nó. Lý do là một ranh giới của chính `endpoints`: trường này khai *"cửa **của `apps/mobile`** mà
   * trang chịu trách nhiệm"*, và gate `assert-ai-coverage` tự khám phá hai đầu — khai một cửa mobile
   * **chưa gọi** ⇒ ĐỎ (*"trang khai cửa mobile không còn gọi"*).
   *
   * Đo được: `grep` trong `apps/mobile/lib` ⇒ chỉ có `expenseApprove` và `expenseVotes`; **`/reject`
   * chưa có caller nào**. Nên trang chat là **caller đầu tiên** của đường từ chối — hợp lệ (AI đi trước
   * GUI), nhưng nó **không thuộc bản đồ cửa-mobile** cho tới khi mobile dựng màn.
   * 🔁 Ngày mobile thêm `expenseReject`: thêm cửa đó vào đây, và **dùng chung** — không mở cửa thứ hai.
   *
   * ✅ QĐ người dùng 12/10/2026 (@UC0035) — ngày đó đã tới: màn YÊU CẦU CHI của mobile gọi `/reject` (từ chối, lý do tuỳ chọn) — đúng
   * cửa binding của trang này đã gọi (`decisionOf`), nên thuộc trang này. `/open-vote` của cùng màn KHÔNG ở đây: binding chat không gọi
   * nó ⇒ khai ở trang redirect `EXP-VOTE` (§4.4 — việc chỉ GUI làm là trang redirect có tên).
   */
  endpoints: [
    "GET /expenses/{expenseId}",
    "POST /expenses/{expenseId}/approve",
    "POST /expenses/{expenseId}/reject",
  ],
  facts: ["tenKhoanChi", "soTien", "tenNguoiUng", "nguoiGui", "dinhKem", "ketQua"],
  templates: {
    // 🔴 0109 S09 (@UC0244): `{tenNguoiUng}` là TÊN hoặc cụm "quỹ trả (không ai ứng)" ⇒ KHÔNG nhúng vào khung "do … ứng" (ảnh máy ảo
    // 25/09: "…do quỹ trả (không ai ứng) ứng?"); câu mở bằng chữ cố định để không bắt đầu bằng chữ thường của ô quyết định.
    // 🔴 AI-EXP-APPROVE-REQUESTS (@UC0035 @UC0066 · QĐ 12/10): YÊU CẦU CHI của thành viên — người duyệt phải thấy AI GỬI (khác người ứng: yêu
    // cầu chi từ quỹ không có người ứng) và biết khoản CÓ TỆP (chat không mở tệp ⇒ chỉ chỗ xem). Hai đoạn tuỳ chọn: rỗng ⇒ không in dòng.
    confirm: "Xác nhận {quyetDinh} khoản chi “{tenKhoanChi}” {soTien}? Người ứng: {tenNguoiUng}.[[ Người gửi: {nguoiGui}.]][[ {dinhKem}]]",
    done: "Đã {quyetDinh} khoản chi “{tenKhoanChi}” ({soTien}; người ứng: {tenNguoiUng}). {ketQua}",
  },
  labels: {
    quyetDinh: "Quyết định",
    tenKhoanChi: "Khoản chi",
    soTien: "Số tiền",
    tenNguoiUng: "Người ứng",
    nguoiGui: "Người gửi",
    dinhKem: "Tệp đính kèm",
    /**
     * 🔴 Ba kết cục, và chỉ `done` biết được (lúc `confirm` thì chưa duyệt nên chưa biết `cooling` hay
     * `posted` — ngưỡng cân nhắc là **cấu hình của quỹ**, resolve lúc ghi).
     */
    ketQua: "Kết quả",
  },
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY"] } } },
  help: { title: "Duyệt khoản chi", tryNow: "duyệt khoản chi tiền nước" },
} satisfies AiCapabilityPage;
